import type { FleetTypeCount, MissionDto } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { CloseIcon, CompassIcon } from '@/components/game/GameIcons'
import { CancelMissionButton } from '@/components/CancelMissionButton'
import { FleetComposition } from '@/components/game/FleetComposition'

/** Vue normalisée d'une flotte en vol (mission ou raid PvP) pour le détail au clic. */
export interface FleetDetailView {
  typeLabel: string
  /** Couleur d'accent du type (cohérente avec le tracé de route). */
  color: string
  destName: string
  /** ISO — pour le compte à rebours live. */
  resolvesAt: string
  fleet: FleetTypeCount[]
  /**
   * Present only when this view represents a mission (not a PvP raid, which
   * has no cancel endpoint) — carried so the panel can offer « Annuler la
   * mission » via the same `isMissionCancellable` gate as the dashboard.
   */
  mission?: MissionDto
}

/** Détail d'une mission/raid en cours : type, destination, ETA live, composition. */
export function MissionDetail({ view, onClose }: { view: FleetDetailView; onClose: () => void }) {
  const remaining = useCountdown(view.resolvesAt)
  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-xl" style={{ color: view.color }}>{view.typeLabel}</h3>
          <p className="text-sm text-ocean-200 flex items-center gap-1.5">
            <CompassIcon className="w-4 h-4 text-ocean-300/70" /> <span aria-hidden="true">→</span> <span className="text-sand-300">{view.destName}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {view.mission && <CancelMissionButton mission={view.mission} />}
          <button onClick={onClose} className="text-ocean-300 hover:text-ocean-100 transition-colors" aria-label="Fermer">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <p className="text-sm text-ocean-100">
        Arrivée dans <span className="font-display tabular-nums text-sand-100">{remaining}</span>
      </p>

      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ocean-300">Flotte engagée</p>
        <FleetComposition fleet={view.fleet} />
      </div>
    </div>
  )
}
